import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AddCartItem } from "@lumosphere/shared";
import { api, type Cart } from "@/lib/api";

/**
 * Le panier vit cote serveur (une commande au statut CART, rattachee a un
 * cookie httpOnly). React Query n en garde qu un cache.
 *
 * C est volontaire : un panier en localStorage ne survit pas au changement
 * d appareil, ne peut pas etre relu par le serveur au checkout, et laisse le
 * navigateur decider des prix. Ici le serveur reste seul maitre des montants.
 */
export const CART_KEY = ["cart"] as const;

export function useCart() {
  return useQuery({
    queryKey: CART_KEY,
    queryFn: () => api.get<Cart>("/cart"),
    // Le panier change souvent et depend de la disponibilite reelle :
    // on ne le laisse pas rassir.
    staleTime: 10_000,
  });
}

export function useAddToCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: AddCartItem) => api.post<Cart>("/cart/items", item),
    onSuccess: (cart) => {
      // La reponse EST le panier a jour : on ecrit le cache directement plutot
      // que de declencher un second aller-retour.
      qc.setQueryData(CART_KEY, cart);
      // La disponibilite affichee ailleurs a pu bouger.
      void qc.invalidateQueries({ queryKey: ["availability"] });
    },
  });
}

export function useUpdateCartLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, quantity }: { lineId: string; quantity: number }) =>
      api.patch<Cart>(`/cart/items/${lineId}`, { quantity }),
    onSuccess: (cart) => qc.setQueryData(CART_KEY, cart),
  });
}

export function useRemoveCartLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: string) => api.delete<Cart>(`/cart/items/${lineId}`),
    onSuccess: (cart) => qc.setQueryData(CART_KEY, cart),
  });
}

/** Nombre d articles, pour la pastille du header. */
export function useCartCount(): number {
  const { data } = useCart();
  return data?.lines.reduce((sum, l) => sum + l.quantity, 0) ?? 0;
}
